import { creditorApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  CreditorI,
  GetCreditorParamsI,
  ListCreditorI,
} from "../../interfaces";
import { AxiosError } from "axios";

const token = () => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  return t;
};

const handleErr = (err: unknown) => {
  const obj = (err as AxiosError).response;
  if (obj?.status === 401) LogoutSystem();
  throw new Error((obj?.data as Error).message);
};

const getCreditors = async ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetCreditorParamsI): Promise<ServiceResponseI<DataI<ListCreditorI>>> => {
  try {
    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (clientId) params.append("clientId", clientId.toString());
    if (status !== undefined) params.append("status", String(status));

    const { data } = await creditorApi.get<ServiceResponseI<DataI<ListCreditorI>>>(
      "",
      {
        params,
        headers: { Authorization: token() },
      },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const getAllCreditors = async (
  clientId?: number,
): Promise<ServiceResponseI<CreditorI[]>> => {
  try {
    const { data } = await creditorApi.get<ServiceResponseI<CreditorI[]>>(
      "/all-no-pagination",
      { params: { clientId }, headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const creditorById = async (
  id: string,
): Promise<ServiceResponseI<CreditorI>> => {
  try {
    const { data } = await creditorApi.get<ServiceResponseI<CreditorI>>(
      `/${id}`,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const creditorPost = async <T>(
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await creditorApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const creditorPut = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await creditorApi.put<ServiceResponseI<string>>(
      `/${id}`,
      payload,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const creditorDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const { data } = await creditorApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: token() } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const useCreditors = ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetCreditorParamsI) => {
  const creditorsQuery = useQuery(
    ["creditors", { pagination, sorting, globalFilter, clientId, status }],
    () => getCreditors({ pagination, sorting, globalFilter, clientId, status }),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { creditorsQuery };
};
